import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { MimeType, Task } from '@repo/shared';
import { useRouter } from '@tanstack/react-router';
import { Brain } from 'lucide-react';
import { toast } from 'sonner';

import { useCreateDescribeImageWithFile } from '@/api/generated';
import type { CreateDescribeImageWithFileRequest } from '@/api/generated.schemas';
import LanguageCombobox from '@/components/language-combobox';
import ModelsCombobox from '@/components/models-combobox';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { FileInput } from '@/components/ui/file-input';
import FileUpload from '@/components/ui/file-upload';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  type CreateDescribeImageWithFileSchema,
  createDescribeImageWithFileSchema,
} from '@/routes/(app)/(tasks)/lib/describe-image-schema';

const DescribeImageWithFile = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { mutate, isPending } = useCreateDescribeImageWithFile();
  const form = useForm<CreateDescribeImageWithFileSchema>({
    defaultValues: {
      files: [],
      models: [],
    },
    resolver: zodResolver(createDescribeImageWithFileSchema),
  });
  const filesWatch = form.watch('files');

  const handleSubmit = useCallback(
    (values: CreateDescribeImageWithFileSchema) => {
      const formData = new FormData();
      // looks wrong, but it is correct.
      values.files.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('data', JSON.stringify(values));

      mutate(
        {
          data: formData as unknown as CreateDescribeImageWithFileRequest,
        },
        {
          onSuccess: async (data) => {
            toast.success(t('clusters.createModelRun.success'));
            await router.navigate({
              to: '/app/clusters',
              search: { datasetId: data.dataset.id, isEval: false },
            });
          },
          onError: () => {
            toast.error(t('clusters.createModelRun.error'));
          },
        }
      );
    },
    [mutate, router, t]
  );

  const handleRemoveFile = useCallback(
    (file: File) => {
      form.setValue(
        'files',
        form.getValues().files.filter((_file) => _file !== file)
      );
    },
    [form]
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card>
          <CardContent className='flex flex-col gap-4'>
            <FormField
              control={form.control}
              name='files'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('datasets.createDataset.file')}</FormLabel>
                  <FormControl>
                    <FileInput
                      {...field}
                      mimeTypes={[
                        MimeType.PNG,
                        MimeType.JPEG,
                        MimeType.GIF,
                        MimeType.WEBP,
                      ]}
                      multiple
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {filesWatch.map((file) => (
              <FileUpload
                key={file.name}
                id={file.name}
                name={file.name}
                size={file.size}
                onRemove={() => {
                  handleRemoveFile(file);
                }}
              />
            ))}
            <FormField
              control={form.control}
              name='models'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.describeImage.form.models.label')}
                  </FormLabel>
                  <FormControl>
                    <ModelsCombobox
                      {...field}
                      task={Task.DESCRIBE_IMAGE}
                      values={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='languageCode'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.describeImage.form.languageCode.label')}
                  </FormLabel>
                  <FormControl>
                    <LanguageCombobox
                      {...field}
                      placeholder={t(
                        'tasks.describeImage.form.languageCode.placeholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='context'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.describeImage.form.context.label')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'tasks.describeImage.form.context.placeholder'
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter>
            <Button
              type='submit'
              disabled={isPending}>
              <Button.Icon side={'left'}>
                {isPending ? <Spinner className='text-inherit' /> : <Brain />}
              </Button.Icon>
              {t('tasks.describeImage.form.submit')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
};

export default DescribeImageWithFile;
